import { useCallback, useEffect, useMemo, useState } from 'react';
import { focusManager } from '@tanstack/react-query';
import { useCacheState } from 'state/useCacheState';
import { useMapState } from 'state/useMapState';
import { IncidentMapStateUpdate } from 'features/mapping/mapping.types';
import { useScrollPositionState } from 'state/useScrollPositionState';
import { useAuthState } from 'state/useAuthState';
import { LatLng } from 'shared/types';
import { Location } from 'features/locations/location.types';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { Flooding } from 'features/flooding/flooding.types';
import { LocationsLayer } from 'features/mapping/components/layers/LocationsLayer';
import { HazardsLayer } from 'features/mapping/components/layers/HazardsLayer';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { useAllGeoEvents } from 'features/geoEvents/hooks/useAllGeoEvents';
import { useSearchDrawerState } from 'state/useSearchDrawerState';
import { useReportsQuery } from 'hooks/useReportsQuery';
import { useReportsPhotosQuery } from 'hooks/useReportsPhotosQuery';
import { useContextMenu } from 'hooks/useContextMenu';
import { useMapLongPress } from 'hooks/useMapLongPress';
import { useMapMode } from 'hooks/useMapMode';
import { useSelectedLocationState } from 'state/useSelectedLocationState';
import {
  ElectricLinesLayer,
  GasPipelineLayer,
} from 'features/mapping/components/layers/TransmissionLinesLayer';
import { Route, Switch } from 'react-router-dom';
import { AlertCamerasLayer } from 'features/mapping/components/layers/AlertCamerasLayer';
import { PrivateLandOwnershipLayer } from 'features/mapping/components/layers/PrivateLandOwnershipLayer';
import { Capacitor } from '@capacitor/core';
import { WaterLevelLayer } from 'features/waterLevel/components/WaterLevelLayer';
import { useWaterLevelEnabled } from 'features/waterLevel/hooks/useWaterLevelEnabled';
import { AllExternalFeatureLayers } from 'features/mapping/components/layers/ExternalFeatureLayer';
import { WeatherStationsLayer } from 'features/weatherStations/components/WeatherStationsLayer';
import { StructuredEvacuationsViewLayer } from 'features/mapping/components/layers/StructuredEvacuationsLayer';
import { WildfiresLayer } from 'features/mapping/components/layers/WildfiresLayer';
import { MapToolsContextMenu } from 'features/mapping/components/MapToolsContextMenu';
import {
  EXTERNAL_GEO_EVENT_ZOOM_LEVEL,
  MapLayers,
} from 'features/mapping/mapping.constants';
import { Map, useMapContext } from 'features/mapping/components/Map';
import { Report, ReportPhoto } from 'features/reports/report.types';
import { ReportsLayer } from 'features/mapping/components/layers/ReportsLayer';
import { PhotoMarkersLayer } from 'features/mapping/components/layers/PhotoMarkersLayer';
import {
  ActiveFloodingsLayer,
  InactiveFloodingsLayer,
} from 'features/mapping/components/layers/FloodingsLayer';
import { useSelectedGeoEventId } from 'features/geoEvents/hooks/useSelectedGeoEventId';
import { useMapEntityDrawer } from 'features/mapping/hooks/useMapEntityDrawer';
import { AnyGeoEvent } from 'features/geoEvents/anyGeoEvent.types';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { MapType } from 'features/mapping/mapTypePolicy';
import { useSelectedExternalGeoEventId } from 'features/geoEvents/hooks/useSelectedExternalGeoEventId';
import { useExternalGeoEvent } from 'hooks/useExternalGeoEvents';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import {
  LockedMapPinActiveProvider,
  useLatLngFromUrl,
} from 'features/mapping/mapPin';
import { CenterMap } from 'features/mapping/components/CenterMap';
import {
  PersistentStatusAlert,
  PersistentStatusAlertVariant,
} from 'components/PersistentStatusAlert';
import { useHasVisibleUnstructuredEvacZones } from 'features/mapping/hooks/useHasVisibleUnstructuredEvacZones';
import { useNetworkStatus } from 'hooks/useNetworkStatus';
import {
  ROUTER_ENTITY_TO_ROUTE_PATH_MAP,
  SEARCH_DRAWER_WIDTH,
} from '../../constants';
import { SearchDrawer } from './SearchDrawer';
import { ClearTimeoutMap } from './ClearTimeoutMap';
import { MapCenterWithPinMarker } from './MapCenterWithPinMarker';
import { useFilteredGeoEvents } from './SearchDrawer/useFilteredGeoEvents';
import { useHoveredModerationReportId } from './SearchDrawer/hooks/useHoveredModerationReportId';
import { useModerationReports } from './SearchDrawer/hooks/useModerationReports';
import { usePhotoModerationTabActive } from './SearchDrawer/hooks/usePhotoModerationTabActive';
import { useIncidentFiltersCount } from '../../state/useIncidentFiltersState';

// Offline takes priority
const getStatusAlertVariant = (options: {
  connected: boolean;
  hasVisibleUnstructuredEvacZones: boolean;
}): PersistentStatusAlertVariant | null => {
  const { connected, hasVisibleUnstructuredEvacZones } = options;

  if (!connected) return 'network';
  if (hasVisibleUnstructuredEvacZones) return 'unstructured_evac_zones';
  return null;
};

const shouldShowHiddenWildfire = (
  isInternalUser: boolean,
  geoEvent?: AnyGeoEvent,
): boolean => {
  if (!geoEvent || geoEvent.isVisible || geoEvent.geoEventType !== 'wildfire') {
    return false;
  }

  // Check for future prescribed incidents for internal users
  if (geoEvent.data.prescribedDateStart && isInternalUser) {
    const prescribedStartDate = new Date(geoEvent.data.prescribedDateStart);
    const currentDate = new Date();

    // Don't show if it's a future prescribed incident - it's already visible on the map
    if (prescribedStartDate.getTime() >= currentDate.getTime()) {
      return false;
    }
  }

  return true;
};

const useStyles = makeStyles<{ open: boolean }>()((theme, { open }) => ({
  root: {
    flex: 1,
    display: 'flex',
  },
  mapContainer: {
    flexGrow: 1,
    position: 'relative',
  },
  mapContainerLargeFormat: {
    flexGrow: 1,
    position: 'relative',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginRight: -SEARCH_DRAWER_WIDTH,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeIn,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginRight: 0,
    }),
  },
}));

type VisibilityToggleableLayersProps = {
  reports: (Report | ReportPhoto)[];
  reportsGeoEventType?: GeoEventType;
};

const VisibilityToggleableLayers = (
  props: VisibilityToggleableLayersProps,
): JSX.Element => {
  const { reports, reportsGeoEventType } = props;
  const { isLayerVisible } = useMapContext();
  const showWaterLevel = useWaterLevelEnabled();
  const isPhotoModerationTabActive = usePhotoModerationTabActive();
  const { reports: moderationReports } = useModerationReports();
  const { hoveredReportId } = useHoveredModerationReportId();

  return (
    <>
      <GasPipelineLayer visible={isLayerVisible(MapLayers.GAS_PIPELINES)} />
      <ElectricLinesLayer
        visible={isLayerVisible(MapLayers.ELECTRICAL_LINES)}
      />
      <PrivateLandOwnershipLayer
        visible={isLayerVisible(MapLayers.PRIVATE_LAND_OWNERSHIP)}
      />
      <AllExternalFeatureLayers />
      <WeatherStationsLayer
        visible={isLayerVisible(MapLayers.WEATHER_STATIONS)}
      />
      <WaterLevelLayer
        visible={isLayerVisible(MapLayers.WATER_LEVEL) && showWaterLevel}
      />
      <Switch>
        <Route
          path={ROUTER_ENTITY_TO_ROUTE_PATH_MAP.camera}
          render={() => (
            <AlertCamerasLayer visible={isLayerVisible(MapLayers.CAMERAS)} />
          )}
        />
        <Route
          path="/"
          render={() => (
            <AlertCamerasLayer visible={isLayerVisible(MapLayers.CAMERAS)} />
          )}
        />
      </Switch>
      {isLayerVisible(MapLayers.REPORTS) && reports.length > 0 && (
        <ReportsLayer reports={reports} geoEventType={reportsGeoEventType} />
      )}
      {isLayerVisible(MapLayers.PHOTO_MARKERS) &&
        isPhotoModerationTabActive && (
          <PhotoMarkersLayer
            reports={moderationReports}
            hoveredReportId={hoveredReportId}
          />
        )}
    </>
  );
};

export const IncidentsMap = (): JSX.Element => {
  const {
    incidentMapState,
    updateIncidentMapState,
    resetReportMapState,
    setActiveMapBounds,
  } = useMapState();
  const { reset: resetScrollState } = useScrollPositionState();
  const {
    permissions: { canReportWildfire, isInternalUser },
    showMembershipProFeatures,
  } = useAuthState();
  const [mapLongPressCoordinates, setMapLongPressCoordinates] =
    useState<LatLng | null>(null);
  const selectedGeoEventId = useSelectedGeoEventId();
  const isMobileApp = Capacitor.isNativePlatform();
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up('tablet'));
  const { open } = useSearchDrawerState();
  const { classes } = useStyles({ open });
  const { selectedLocation, setSelectedLocation } = useSelectedLocationState();
  const { isTimelineMode, setMapMode } = useMapMode();
  const { setCacheState } = useCacheState();
  const { latitudeParam, longitudeParam, zoomLevelParam } = useLatLngFromUrl();
  const { minimizeEntityDrawer } = useMapEntityDrawer();
  const pinImprovements = useReleaseFlag('pin_drop_improvements');

  useEffect(() => {
    // On web, we need to invalidate the cache whenever the selected geoEventId changes
    // to avoid showing stale data due to push notifications never invalidating the cache
    if (!Capacitor.isNativePlatform() && selectedGeoEventId) {
      // round down to the second so we have some form of cache hit similarity
      setCacheState(Math.round(Date.now() / 1000) * 1000);
    }
  }, [selectedGeoEventId, setCacheState]);

  // Leave timeline mode when navigating to a geo event
  useEffect(() => {
    if (selectedGeoEventId && isTimelineMode) {
      setMapMode('default');
    }
  }, [selectedGeoEventId, isTimelineMode, setMapMode]);

  // Because we use history.goBack from the addIncident form - we need to set focus on the page to ensure
  // we refetch the tiles query and reporters see the newly created incident.
  // Otherwise the query uses the disk cache and does not refetch, even with modifications to gcTime/staleTime
  useEffect(() => {
    if (canReportWildfire) {
      focusManager.setFocused(true);
    }
  }, [canReportWildfire]);

  const { allGeoEvents, wildfireEvents, locations, floodingEvents, hazards } =
    useAllGeoEvents();

  const { geoEvent: selectedGeoEvent } = useGeoEvent<AnyGeoEvent>({
    id: selectedGeoEventId,
    useCacheBuster: true,
  });

  const incidentFiltersCount = useIncidentFiltersCount();

  const combinedGeoEvents = useMemo(
    () => [...wildfireEvents, ...floodingEvents],
    [wildfireEvents, floodingEvents],
  );

  const { connected } = useNetworkStatus();
  const hasVisibleUnstructuredEvacZones =
    useHasVisibleUnstructuredEvacZones(combinedGeoEvents);

  const statusAlertVariant = getStatusAlertVariant({
    connected,
    hasVisibleUnstructuredEvacZones,
  });

  const filteredGeoEvents = useFilteredGeoEvents({
    geoEvents: combinedGeoEvents,
  });

  const filteredGeoEventIds = useMemo(
    () =>
      incidentFiltersCount > 0
        ? new Set(filteredGeoEvents.map((geoEvent) => geoEvent.id))
        : undefined,
    [filteredGeoEvents, incidentFiltersCount],
  );

  const selectedExternalGeoEventId = useSelectedExternalGeoEventId();
  const { externalGeoEvent: selectedExternalGeoEvent } = useExternalGeoEvent(
    selectedExternalGeoEventId,
  );

  // Use the list data to speed up the re-center behavior when you get to the map so we don't have to wait for the
  // second query to possibly finish
  const selectedLatLng: LatLng | null = useMemo(() => {
    const listGeoEvent = allGeoEvents.find(
      (item) => item?.id === selectedGeoEventId,
    );
    if (listGeoEvent) {
      return { lat: listGeoEvent.lat, lng: listGeoEvent.lng };
    }
    if (selectedGeoEvent) {
      return { lat: selectedGeoEvent.lat, lng: selectedGeoEvent.lng };
    }
    if (selectedExternalGeoEvent) {
      return {
        lat: selectedExternalGeoEvent.lat,
        lng: selectedExternalGeoEvent.lng,
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGeoEventId, selectedGeoEvent, selectedExternalGeoEvent]);

  const reportsQuery = useReportsQuery(selectedGeoEventId);

  const showReportPhotos = useReleaseFlag('show_report_photos_on_map');

  const userPhotosQuery = useReportsPhotosQuery({
    enabled: !selectedGeoEventId && showReportPhotos,
  });

  const reports = selectedGeoEventId
    ? (reportsQuery.data?.data ?? [])
    : (userPhotosQuery.data?.data ?? []);

  // Resetting ReportMap State so that it re-centers correctly
  // when viewing the report map.
  useEffect(() => {
    resetReportMapState();
    resetScrollState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update incident map state when the user pan / zooms the map
  // and set tiles so we re-query for new geo-events
  const onViewportChange = useCallback(
    (viewport: IncidentMapStateUpdate) => {
      updateIncidentMapState(viewport);
      setActiveMapBounds(viewport.bounds);
    },
    [updateIncidentMapState, setActiveMapBounds],
  );

  // @todo remove when removing 'pin_drop_improvements' release flag
  const handleMapClick = useCallback(() => {
    if (selectedLocation) {
      setSelectedLocation(null);
    }
  }, [selectedLocation, setSelectedLocation]);

  const handleMapLongPress = useCallback(
    (coordinates: LatLng): void => {
      if (!showMembershipProFeatures && !pinImprovements) {
        return;
      }
      setMapLongPressCoordinates(coordinates);
    },
    [showMembershipProFeatures, pinImprovements],
  );

  const { ctxMenuClose, contextMenuCoordinates, openContextMenu } =
    useContextMenu();

  const { onTouchStart, clearMobileTimeout } = useMapLongPress({
    onLongPress: handleMapLongPress,
  });

  // in the case of an inactive geo event linked from google search or direct link, we're no longer
  // returning that data in the list view, so we manually add this icon onto the map
  const showHiddenWildFire = useMemo(
    () => shouldShowHiddenWildfire(isInternalUser, selectedGeoEvent),
    [isInternalUser, selectedGeoEvent],
  );

  const showHiddenLocation =
    selectedGeoEvent &&
    selectedGeoEvent.geoEventType === 'location' &&
    !selectedGeoEvent.isVisible;

  const showHiddenFlooding =
    selectedGeoEvent &&
    selectedGeoEvent.geoEventType === 'flooding' &&
    !selectedGeoEvent.isVisible;

  const containerClass = isLargeMediaQuery
    ? classes.mapContainerLargeFormat
    : classes.mapContainer;

  // When URL specifies coordinates, seed the Map's initialViewState with the URL
  // coordinates so the map starts at the right position immediately instead of
  // animating from the previously-stored center.
  const center = useMemo(
    () => ({
      lat: latitudeParam ?? incidentMapState.center.lat,
      lng: longitudeParam ?? incidentMapState.center.lng,
    }),
    [
      latitudeParam,
      longitudeParam,
      incidentMapState.center.lat,
      incidentMapState.center.lng,
    ],
  );

  return (
    <LockedMapPinActiveProvider>
      <Box className={classes.root}>
        <Box
          className={containerClass}
          onMouseDown={isMobileApp ? undefined : () => minimizeEntityDrawer()}
          onTouchStart={isMobileApp ? () => minimizeEntityDrawer() : undefined}
          onContextMenu={(e) => {
            // It's been added to prevent opening native context menu when MUI menu was open.
            e.preventDefault();
          }}
        >
          <Map
            mapType={MapType.Main}
            center={center}
            zoom={zoomLevelParam ?? incidentMapState.zoom}
            onViewportChange={onViewportChange}
            onContextMenu={openContextMenu}
            onTouchStart={onTouchStart}
            onClick={pinImprovements ? undefined : handleMapClick}
            withSearchDrawer
          >
            <StructuredEvacuationsViewLayer />
            <VisibilityToggleableLayers
              reports={reports}
              reportsGeoEventType={selectedGeoEvent?.geoEventType}
            />
            {locations && (
              <LocationsLayer
                locations={locations}
                selectedGeoEventId={selectedGeoEventId}
              />
            )}
            {hazards && (
              <HazardsLayer
                hazards={hazards}
                selectedGeoEventId={selectedGeoEventId}
              />
            )}
            {floodingEvents && (
              <InactiveFloodingsLayer
                floodingEvents={floodingEvents}
                isFadable
                emphasizedGeoEventIds={filteredGeoEventIds}
                selectedGeoEventId={selectedGeoEventId}
              />
            )}
            {wildfireEvents && (
              <WildfiresLayer
                wildfires={wildfireEvents}
                isFadable
                selectedGeoEventId={selectedGeoEventId}
                emphasizedGeoEventIds={filteredGeoEventIds}
              />
            )}
            {floodingEvents && (
              <ActiveFloodingsLayer
                floodingEvents={floodingEvents}
                isFadable
                emphasizedGeoEventIds={filteredGeoEventIds}
                selectedGeoEventId={selectedGeoEventId}
              />
            )}
            {showHiddenWildFire && (
              <Map.WildFireGeoEventMarker
                geoEvent={selectedGeoEvent as unknown as Wildfire}
              />
            )}
            {showHiddenLocation && (
              <Map.LocationMarker
                location={selectedGeoEvent as unknown as Location}
              />
            )}
            {showHiddenFlooding && (
              <Map.FloodingGeoEventMarker
                geoEvent={selectedGeoEvent as unknown as Flooding}
              />
            )}
            <MapCenterWithPinMarker />
            <Map.MapEvents onViewportChange={onViewportChange} />
            <CenterMap
              zoomIn
              latLng={selectedLatLng}
              zoom={
                selectedExternalGeoEvent
                  ? EXTERNAL_GEO_EVENT_ZOOM_LEVEL
                  : undefined
              }
              drawerIsOpen
              defaultSnapPointPct={0.47}
            />
            <ClearTimeoutMap clearMobileTimeout={clearMobileTimeout} />
            <MapToolsContextMenu
              // This instance is for the "right-click" context menu.
              variant="menu"
              onClose={ctxMenuClose}
              pointCoordinates={contextMenuCoordinates}
              anchorPosition={
                (contextMenuCoordinates && {
                  top: contextMenuCoordinates.mouseY,
                  left: contextMenuCoordinates.mouseX,
                }) || { top: 0, left: 0 }
              }
              exclude={isTimelineMode ? ['measureDistance'] : []}
            />
            <MapToolsContextMenu
              // This instance is for map long-presses.
              variant="drawer"
              onClose={() => {
                setMapLongPressCoordinates(null);
              }}
              pointCoordinates={mapLongPressCoordinates}
              exclude={isTimelineMode ? ['measureDistance'] : []}
            />
          </Map>

          <PersistentStatusAlert
            variant={statusAlertVariant}
            trackBottomSheetHeight={!isLargeMediaQuery}
          />
        </Box>

        <SearchDrawer />
      </Box>
    </LockedMapPinActiveProvider>
  );
};
